// src/services/apiService.js

import axios from 'axios';

/**
 * Set base URL for axios.
 * In development, detect if we're using dev tunnels and adjust accordingly.
 * In production, use the correct backend URL.
 */
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'development') {
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;
    
    // Check if we're accessing via dev tunnels (contains devtunnels.ms)
    if (currentHost.includes('devtunnels.ms')) {
      // Extract the tunnel base and replace port 3000 with 8000
      const currentUrl = window.location.origin;
      const backendUrl = currentUrl.replace('-3000.', '-8000.');
      return backendUrl;
    }
    
    // Default local development - use proxy
    return '';
  }
  
  // Production - check if there's a custom API base URL in environment
  // If not, use the same origin as the frontend (assuming backend is on same domain)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // For newlightai.ge, this will be https://newlightai.ge
  return window.location.origin;
};

const BASE_URL = getBaseURL();

/**
 * Fetch content data for a given slug.
 * @param {string} slug - The content slug.
 * @returns {Promise<any>} - The response data.
 */
export const getContent = async (slug) => {
  const response = await axios.get(`${BASE_URL}/api/content/${slug}/`);
  return response.data;
};

/**
 * Upload an image for processing.
 * @param {FormData} formData - The form data containing the image.
 * @returns {Promise<any>} - The axios response.
 */
export const uploadImage = async (formData) => {
  const response = await axios.post(`${BASE_URL}/api/upload/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

/**
 * Scan a barcode and retrieve product data.
 * @param {string} barcode - The barcode to scan.
 * @param {boolean} [skipLog=false] - Whether to skip creating a new SearchLog in the backend.
 * @param {AbortSignal} [signal] - Optional AbortController signal for cancellation.
 * @returns {Promise<any>} - The axios response.
 */
export const scanBarcode = async (barcode, skipLog = false, signal) => {
  // ADDED: pass skipLog param to skip creating a new SearchLog in the backend
  const response = await axios.post(
    `${BASE_URL}/api/scan_barcode/`,
    { barcode, skipLog },
    { signal } // Pass the AbortController signal if available
  );
  return response;
};

/**
 * Get 3D model data for a product.
 * @param {string} productId - The product ID.
 * @param {string} [version] - Optional version parameter.
 * @returns {Promise<any>} - The axios response.
 */
export const get3DModel = async (productId, version) => {
  let url;
  
  // Check if we're in dev tunnel mode
  if (window.location.hostname.includes('devtunnels.ms')) {
    // Use the backend dev tunnel URL directly
    const backendUrl = window.location.origin.replace('-3000.', '-8000.');
    url = `${backendUrl}/api/product3d?productId=${productId}`;
  } else {
    // Use proxy for local development
    url = `/api/product3d?productId=${productId}`;
  }
  
  if (version) {
    url += `&version=${version}`;
  }
  
  const response = await axios.get(url);
  return response;
};

/**
 * Get list of available 3D models.
 * @returns {Promise<any>} - The axios response.
 */
export const get3DModelsList = async () => {
  let url;
  
  // Check if we're in dev tunnel mode
  if (window.location.hostname.includes('devtunnels.ms')) {
    // Use the backend dev tunnel URL directly
    const backendUrl = window.location.origin.replace('-3000.', '-8000.');
    url = `${backendUrl}/api/product3d`;
  } else {
    // Use proxy for local development
    url = '/api/product3d';
  }
  
  const response = await axios.get(url);
  return response;
};
